import React, { useMemo } from 'react';
import * as THREE from 'three';
import { FOUP } from './amhs/FOUP/FOUP';
import {
  resolveFoupKindColor,
  resolveKitFoupScale,
  type FoupKind,
} from './amhs/types';
import type { OhtTipChip, SimulationPhaseChip } from '../../utils/simulationPhaseUi';
import { OhtPhaseIndicator3D } from './OhtPhaseIndicator3D';
import { canLowerOhtHoist } from '../../utils/ohtHoist';
import {
  defaultElementDepth,
  OHT_MODEL_SCALE_MULTIPLIER,
  OHT_MODEL_Y_OFFSET,
} from '../../utils/layout3d';
import { getDualRailVerticalBounds } from './DualRailTrack3D';
import { getBliLogoTexture } from '../../utils/bliLogoTexture';
import {
  OHT_TRACK_DROP_RELEASE_PROGRESS,
  OHT_TRACK_PICKUP_TAKE_PROGRESS,
} from '../../features/simulation/ohtCarriedFoupVisible';

export { OHT_MODEL_SCALE_MULTIPLIER } from '../../utils/layout3d';

/** Pause between the vehicle stopping at the node and the hoist lowering. */
export const HOIST_SETTLE_SEC = 1;
export const HOIST_SETTLE_MAX_FRACTION = 0.15;

interface OhtVehicle3DModelProps {
  w: number;
  h: number;
  depth: number;
  opacity: number;
  paused?: boolean;
  groupY?: number;
  isPaused?: boolean;
  stopElapsedS?: number;
  stopDurationS?: number | null;
  stopLoadedAction?: boolean;
  hoistSettleS?: number;
  transferDeckY?: number;
  isLoaded?: boolean;
  foupKind?: FoupKind;
  status?: SimulationPhaseChip;
  tipChip?: OhtTipChip;
  statusLabel?: string;
  showStatusBadge?: boolean;
  pivotX?: number;
  pivotY?: number;
}

export const FOUP_REF_WIDTH = 8.0;
export const FOUP_REF_HEIGHT = 4.5;

export const FoupBox3D: React.FC<{
  w?: number;
  h?: number;
  opacity: number;
  S: number;
  foupKind?: FoupKind;
}> = ({ opacity, foupKind }) => {
  const foupScale = resolveKitFoupScale();

  return (
    <FOUP
      scale={foupScale}
      position={[0, 0, 0]}
      opacity={opacity}
      color={resolveFoupKindColor(foupKind)}
    />
  );
};

/**
 * Precision Wheel Bogie Unit matching 100% of reference image:
 * 1. Dark Anodized Stepped Mounting Block on track rail with front Allen bolt (silver collar + hex socket) & top counterbore recess.
 * 2. CNC Machined Satin Stainless Steel Fork Bracket with 2 micro Allen screws and dual vertical fork tines.
 * 3. Precision Machined Stainless Steel Grooved Roller Wheel with chamfered rim, contact tread, and center axle pin.
 */
const WheelBogieUnit: React.FC<{
  wheelRadius: number;
  wheelWidth: number;
  sideSign: number; // -1 for back face (-Z), +1 for front face (+Z)
  outerDirSign: number; // -1 for rear bogie (block on -X), +1 for front bogie (block on +X)
  S: number;
}> = ({ wheelRadius, wheelWidth, sideSign, outerDirSign, S }) => {
  const blockX = outerDirSign * 0.22 * S;
  const forkX = 0;
  const zOffset = sideSign * (wheelWidth * 0.5);

  return (
    <group>
      {/* ========================================================================= */}
      {/* 1. DARK ANODIZED STEPPED MOUNTING BLOCK (Riding on top of Track Rail)     */}
      {/* ========================================================================= */}
      <group position={[blockX, 0.13 * S, zOffset]}>
        {/* Main Black Mounting Block */}
        <mesh castShadow receiveShadow>
          <boxGeometry args={[0.24 * S, 0.22 * S, 0.16 * S]} />
          <meshStandardMaterial
            color="#181B20"
            metalness={0.88}
            roughness={0.25}
            envMapIntensity={1.2}
          />
        </mesh>

        {/* Top Vertical Counterbore Recess Hole */}
        <group position={[0, 0.111 * S, 0]} rotation={[0, 0, 0]}>
          {/* Silver Chamfered Counterbore Bezel Ring */}
          <mesh>
            <cylinderGeometry args={[0.042 * S, 0.042 * S, 0.004 * S, 20]} />
            <meshStandardMaterial color="#94A3B8" metalness={0.96} roughness={0.15} />
          </mesh>
          {/* Dark Inner Bore Hole */}
          <mesh position={[0, 0.001 * S, 0]}>
            <cylinderGeometry args={[0.030 * S, 0.030 * S, 0.006 * S, 16]} />
            <meshStandardMaterial color="#0A0C0E" metalness={0.7} roughness={0.5} />
          </mesh>
        </group>

        {/* Front Socket-Head Cap Screw (Allen Bolt) */}
        <group
          position={[0, 0, sideSign * (0.08 * S + 0.001 * S)]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          {/* Raised Silver Washer Flange Collar */}
          <mesh position={[0, 0, sideSign * 0.006 * S]}>
            <cylinderGeometry args={[0.050 * S, 0.050 * S, 0.012 * S, 24]} />
            <meshStandardMaterial
              color="#CBD5E1"
              metalness={0.97}
              roughness={0.12}
              envMapIntensity={2.0}
            />
          </mesh>
          {/* Cylindrical Bolt Head */}
          <mesh position={[0, 0, sideSign * 0.018 * S]}>
            <cylinderGeometry args={[0.040 * S, 0.040 * S, 0.016 * S, 24]} />
            <meshStandardMaterial
              color="#E2E8F0"
              metalness={0.98}
              roughness={0.10}
              envMapIntensity={2.2}
            />
          </mesh>
          {/* Inner Hexagonal Drive Socket Recess */}
          <mesh position={[0, 0, sideSign * 0.026 * S]}>
            <cylinderGeometry args={[0.020 * S, 0.020 * S, 0.008 * S, 6]} />
            <meshStandardMaterial color="#0B0D11" metalness={0.9} roughness={0.4} />
          </mesh>
        </group>
      </group>

      {/* ========================================================================= */}
      {/* 2. CNC MACHINED SATIN STAINLESS STEEL FORK BRACKET (Bogie Yoke)           */}
      {/* ========================================================================= */}
      <group position={[forkX, 0.06 * S, zOffset]}>
        {/* Upper Solid Mounting Block */}
        <mesh position={[0, 0.065 * S, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.18 * S, 0.15 * S, 0.15 * S]} />
          <meshStandardMaterial
            color="#D4D9E1"
            metalness={0.96}
            roughness={0.15}
            envMapIntensity={2.2}
          />
        </mesh>

        {/* Dual Micro Socket-Head Allen Screws on Face */}
        {[-0.042 * S, 0.042 * S].map((sx, sIdx) => (
          <group
            key={`fork-screw-${sIdx}`}
            position={[sx, 0.035 * S, sideSign * (0.075 * S + 0.002 * S)]}
            rotation={[Math.PI / 2, 0, 0]}
          >
            {/* Silver Screw Head */}
            <mesh position={[0, 0, sideSign * 0.004 * S]}>
              <cylinderGeometry args={[0.022 * S, 0.022 * S, 0.008 * S, 16]} />
              <meshStandardMaterial color="#CBD5E1" metalness={0.98} roughness={0.10} />
            </mesh>
            {/* Dark Hex Socket Center */}
            <mesh position={[0, 0, sideSign * 0.008 * S]}>
              <cylinderGeometry args={[0.010 * S, 0.010 * S, 0.004 * S, 6]} />
              <meshStandardMaterial color="#0F172A" metalness={0.8} roughness={0.4} />
            </mesh>
          </group>
        ))}

        {/* Outer Vertical Fork Leg */}
        <mesh
          position={[0, -0.045 * S, sideSign * 0.065 * S]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[0.075 * S, 0.18 * S, 0.024 * S]} />
          <meshStandardMaterial
            color="#D1D5DB"
            metalness={0.96}
            roughness={0.14}
            envMapIntensity={2.0}
          />
        </mesh>

        {/* Inner Vertical Fork Leg */}
        <mesh
          position={[0, -0.045 * S, -sideSign * 0.065 * S]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[0.075 * S, 0.18 * S, 0.024 * S]} />
          <meshStandardMaterial
            color="#D1D5DB"
            metalness={0.96}
            roughness={0.14}
            envMapIntensity={2.0}
          />
        </mesh>
      </group>

      {/* ========================================================================= */}
      {/* 3. PRECISION MACHINED STEEL GROOVED ROLLER WHEEL                          */}
      {/* ========================================================================= */}
      <group position={[forkX, 0, zOffset]}>
        {/* Outer Machined Steel Wheel Rim with Polished Chamfer */}
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[wheelRadius, wheelRadius, wheelWidth * 0.88, 36]} />
          <meshStandardMaterial
            color="#F1F5F9"
            metalness={0.98}
            roughness={0.08}
            envMapIntensity={2.5}
          />
        </mesh>

        {/* Precision Stepped Contact Tread (Central Running Rim) */}
        <mesh rotation={[Math.PI / 2, 0, 0]} receiveShadow>
          <cylinderGeometry
            args={[wheelRadius * 0.94, wheelRadius * 0.94, wheelWidth * 0.55, 32]}
          />
          <meshStandardMaterial
            color="#CBD5E1"
            metalness={0.96}
            roughness={0.16}
            envMapIntensity={2.0}
          />
        </mesh>

        {/* Dark Recessed Inner Channel Step */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry
            args={[wheelRadius * 0.72, wheelRadius * 0.72, wheelWidth * 0.92, 28]}
          />
          <meshStandardMaterial color="#1E293B" metalness={0.85} roughness={0.32} />
        </mesh>

        {/* Precision Axle Pin & Chamfered End Cap */}
        <group rotation={[Math.PI / 2, 0, 0]}>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry
              args={[wheelRadius * 0.28, wheelRadius * 0.28, wheelWidth * 1.55, 20]}
            />
            <meshStandardMaterial color="#CBD5E1" metalness={0.96} roughness={0.12} />
          </mesh>
          {/* Polished Axle Cap on Outer Face */}
          <mesh position={[0, 0, sideSign * (wheelWidth * 0.80)]}>
            <cylinderGeometry
              args={[wheelRadius * 0.36, wheelRadius * 0.36, 0.015 * S, 20]}
            />
            <meshStandardMaterial
              color="#E2E8F0"
              metalness={0.98}
              roughness={0.08}
              envMapIntensity={2.2}
            />
          </mesh>
        </group>
      </group>
    </group>
  );
};

/**
 * Pixel-Perfect "BLI" Logo Component matching 100% of user reference image.
 * Displays the cyan-blue 4-petal diamond emblem + bold "BLI" typography on the white right leg.
 * Positioned on the exterior bevel surface with anisotropic sampling so it stays crisp and visible at all zoom levels.
 */
const BliLogo3D: React.FC<{
  podL: number;
  podW: number;
  facingSign: 1 | -1;
  opacity: number;
  S: number;
}> = ({ podL, podW, facingSign, opacity, S }) => {
  const bliTexture = useMemo(() => getBliLogoTexture(), []);
  const bevelThick = Math.min(0.065 * S, podW * 0.07);
  const outerZ = podW * 0.5 + bevelThick;

  if (!bliTexture) return null;

  return (
    <group
      position={[0, -0.01 * S, facingSign * (outerZ + 0.005 * S)]}
      rotation={[0, facingSign === 1 ? 0 : Math.PI, 0]}
    >
      <mesh renderOrder={999} frustumCulled={false}>
        <planeGeometry args={[podL * 0.70, podL * 0.70]} />
        <meshBasicMaterial
          map={bliTexture}
          transparent
          opacity={opacity}
          depthTest={true}
          depthWrite={false}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
};

/**
 * Compact Rounded White Lower Module/Pod ("Legs").
 * Features ultra-smooth glossy cleanroom finish, continuous 3D bevels, top mounting gasket,
 * and authentic reference detailing (3D BLI emblem or debossed panel & side latch button).
 */
const WhiteLowerPod: React.FC<{
  length: number;
  height: number;
  width: number;
  opacity: number;
  badge?: 'BLI' | 'recess';
  facingSign?: 1 | -1;
  S: number;
}> = ({ length, height, width, opacity, badge, facingSign = 1, S }) => {
  const podGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    const halfL = length * 0.5;
    const halfH = height * 0.5;
    const cornerR = Math.min(0.32 * S, length * 0.28, height * 0.28);

    shape.moveTo(-halfL + cornerR, halfH);
    shape.lineTo(halfL - cornerR, halfH);
    shape.absarc(halfL - cornerR, halfH - cornerR, cornerR, Math.PI / 2, 0, true);
    shape.lineTo(halfL, -halfH + cornerR);
    shape.absarc(halfL - cornerR, -halfH + cornerR, cornerR, 0, -Math.PI / 2, true);
    shape.lineTo(-halfL + cornerR, -halfH);
    shape.absarc(-halfL + cornerR, -halfH + cornerR, cornerR, -Math.PI / 2, -Math.PI, true);
    shape.lineTo(-halfL, halfH - cornerR);
    shape.absarc(-halfL + cornerR, halfH - cornerR, cornerR, Math.PI, Math.PI / 2, true);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: width,
      bevelEnabled: true,
      bevelSegments: 8,
      steps: 1,
      bevelSize: Math.min(0.065 * S, width * 0.07),
      bevelThickness: Math.min(0.065 * S, width * 0.07),
    };

    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geom.center();
    return geom;
  }, [length, height, width, S]);

  return (
    <group>
      {/* Main Smoothly Curved Ultra-Glossy White Cleanroom Pod */}
      <mesh geometry={podGeometry} castShadow receiveShadow>
        <meshPhysicalMaterial
          color="#FFFFFF"
          metalness={0.04}
          roughness={0.10}
          clearcoat={1.0}
          clearcoatRoughness={0.06}
          envMapIntensity={1.8}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Top Black Mounting Gasket Interface */}
      <mesh position={[0, height * 0.5 + 0.015 * S, 0]}>
        <boxGeometry args={[length * 0.94, 0.03 * S, width * 0.94]} />
        <meshStandardMaterial color="#1E2328" metalness={0.85} roughness={0.25} />
      </mesh>

      {/* Flank Pill-Shaped Release Latch Button (Facing outer edge / wheel bogie) */}
      <group
        position={[
          facingSign * (-length * 0.42),
          height * 0.12,
          facingSign * (width * 0.5 + 0.015 * S),
        ]}
      >
        {/* Soft Grey Recessed Bezel Frame */}
        <mesh position={[0, 0, -0.004 * S]}>
          <boxGeometry args={[0.11 * S, height * 0.38, 0.012 * S]} />
          <meshStandardMaterial color="#E2E8F0" roughness={0.35} metalness={0.05} />
        </mesh>
        {/* Smooth Pill-Shaped White Button with Rounded Ends */}
        <mesh castShadow receiveShadow>
          <boxGeometry args={[0.08 * S, height * 0.32, 0.024 * S]} />
          <meshPhysicalMaterial
            color="#FFFFFF"
            roughness={0.12}
            clearcoat={1.0}
            clearcoatRoughness={0.08}
          />
        </mesh>
      </group>

      {/* Right Pod: High-Fidelity 3D "BLI" Badge + 4-Petal Cyan Emblem */}
      {badge === 'BLI' && (
        <BliLogo3D
          podL={length}
          podW={width}
          facingSign={facingSign}
          opacity={opacity}
          S={S}
        />
      )}

      {/* Left Pod: Square Recessed Panel */}
      {badge === 'recess' && (
        <group rotation={[0, facingSign === 1 ? 0 : Math.PI, 0]}>
          {/* Shallow Square Recessed Panel with Inner Frame */}
          <mesh position={[0.04 * S, -0.04 * S, width * 0.5 + 0.008 * S]}>
            <boxGeometry args={[length * 0.55, height * 0.58, 0.015 * S]} />
            <meshStandardMaterial color="#E2E8F0" roughness={0.28} metalness={0.06} />
          </mesh>
          <mesh position={[0.04 * S, -0.04 * S, width * 0.5 + 0.016 * S]}>
            <boxGeometry args={[length * 0.48, height * 0.50, 0.008 * S]} />
            <meshStandardMaterial color="#F1F5F9" roughness={0.20} metalness={0.04} />
          </mesh>
        </group>
      )}
    </group>
  );
};

/**
 * 100% Faithful Industrial OHT Vehicle 3D Replica based directly on Multi-View Reference Sheet.
 *
 * Structure:
 * - UPPER HOUSING: Glossy rounded black aerodynamic chassis with recessed pill-shaped status controls.
 * - TOP COVER: Transparent polycarbonate bubble housing with 4 fluted corner towers, white nylon guide pins, and inner motor turntable.
 * - FOUR WHEEL BOGIES: Metallic roller wheels with silver hubs, dark stepped mounting blocks, and micro Allen bolts riding directly on top of the track rail.
 * - TWO WHITE LOWER MODULES: Symmetrical compact rounded white pods ("BLI" cyan emblem + debossed panel) hanging down along the track beam.
 * - CENTER CLAMP: Anodized silver mounting bridge connecting the pods.
 */
export const OhtVehicle3DModel: React.FC<OhtVehicle3DModelProps> = React.memo(({
  w,
  h,
  depth: _depth,
  opacity,
  groupY: _groupY = 0,
  isPaused = false,
  stopElapsedS = 0,
  stopDurationS = null,
  stopLoadedAction,
  transferDeckY,
  isLoaded = false,
  foupKind,
  status = 'idle',
  tipChip,
  statusLabel,
  showStatusBadge = true,
  pivotX,
  pivotY,
}) => {
  const S = OHT_MODEL_SCALE_MULTIPLIER;
  const vScale = S / 3.5;

  const pX = pivotX !== undefined ? pivotX : w / 2;
  const pY = pivotY !== undefined ? pivotY : h / 2;

  const railProfileDepth = defaultElementDepth('overhead-rail');
  const { topY: railTopY } = getDualRailVerticalBounds(railProfileDepth);
  const trackTopY = railTopY + OHT_MODEL_Y_OFFSET;
  const trackRailW = 0.96; // Track beam width
  const trackHalfW = trackRailW * 0.5; // 0.48

  // 1. Fixed Proportions matching Industrial Reference with slightly wider stance:
  const bodyL = 4.85 * S; // length along travel axis
  const bodyW = 3.52 * S; // width across track (gauge span)
  const chassisH = 0.92 * S;

  // Wheel positioning (outer ends flanking the white pods):
  const wheelRadius = 0.16 * S;
  const wheelWidth = 0.075 * S;
  const wheelY = trackTopY + wheelRadius;
  const wheelFrontX = bodyL * 0.42;
  const wheelRearX = -bodyL * 0.42;

  // Chassis sits tight on bogies — minimal air gap above the rail riding surface.
  const chassisBaseY = wheelY + 0.12 * S;
  const chassisCenterY = chassisBaseY + chassisH * 0.5;

  // 2. White lower pods — deep, wide, robust modules matching reference image
  const podL = 1.45 * S;
  const podW = 0.72 * S;
  const podH = 1.35 * S;
  const podOffsetZ = trackHalfW + podW * 0.5 + 0.02 * S;
  const podCenterY = chassisBaseY - podH * 0.5 + 0.04 * S;
  const podFrontX = bodyL * 0.22;
  const podRearX = -bodyL * 0.22;

  // 3. Rounded Chassis Geometry (Compact rounded rectangle with bevel)
  const chassisGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    const halfL = bodyL * 0.5;
    const halfW = bodyW * 0.5;
    const cornerR = 0.32 * S;

    shape.moveTo(-halfL + cornerR, halfW);
    shape.lineTo(halfL - cornerR, halfW);
    shape.absarc(halfL - cornerR, halfW - cornerR, cornerR, Math.PI / 2, 0, true);
    shape.lineTo(halfL, -halfW + cornerR);
    shape.absarc(halfL - cornerR, -halfW + cornerR, cornerR, 0, -Math.PI / 2, true);
    shape.lineTo(-halfL + cornerR, -halfW);
    shape.absarc(-halfL + cornerR, -halfW + cornerR, cornerR, -Math.PI / 2, -Math.PI, true);
    shape.lineTo(-halfL, halfW - cornerR);
    shape.absarc(-halfL + cornerR, halfW - cornerR, cornerR, Math.PI, Math.PI / 2, true);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: chassisH,
      bevelEnabled: true,
      bevelSegments: 4,
      steps: 1,
      bevelSize: 0.035 * S,
      bevelThickness: 0.035 * S,
    };

    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geom.center();
    geom.rotateX(Math.PI / 2);
    return geom;
  }, [bodyL, bodyW, chassisH, S]);

  // 4. Transparent Top Cover Geometry (Centered square-rounded dome)
  const topCoverGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    const coverL = bodyL * 0.72;
    const coverW = bodyW * 0.75;
    const halfL = coverL * 0.5;
    const halfW = coverW * 0.5;
    const cornerR = 0.18 * S;

    shape.moveTo(-halfL + cornerR, halfW);
    shape.lineTo(halfL - cornerR, halfW);
    shape.absarc(halfL - cornerR, halfW - cornerR, cornerR, Math.PI / 2, 0, true);
    shape.lineTo(halfL, -halfW + cornerR);
    shape.absarc(halfL - cornerR, -halfW + cornerR, cornerR, 0, -Math.PI / 2, true);
    shape.lineTo(-halfL + cornerR, -halfW);
    shape.absarc(-halfL + cornerR, -halfW + cornerR, cornerR, -Math.PI / 2, -Math.PI, true);
    shape.lineTo(-halfL, halfW - cornerR);
    shape.absarc(-halfL + cornerR, halfW - cornerR, cornerR, Math.PI, Math.PI / 2, true);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: 0.20 * S,
      bevelEnabled: true,
      bevelSegments: 3,
      steps: 1,
      bevelSize: 0.02 * S,
      bevelThickness: 0.02 * S,
    };

    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geom.center();
    geom.rotateX(Math.PI / 2);
    return geom;
  }, [bodyL, bodyW, S]);

  const coverTopLocal = chassisH * 0.5 + 0.22 * S;
  const vehicleBodyTopY = chassisCenterY + coverTopLocal + 0.35 * S;

  const transferDuration =
    isPaused &&
    stopDurationS !== null &&
    stopDurationS > 0 &&
    stopLoadedAction !== undefined
      ? stopDurationS
      : null;
  const isTransferActive = canLowerOhtHoist(transferDuration, transferDeckY);

  // Parent `isLoaded` follows resolveOhtCarriedFoupVisible (ledger + transfer beats).
  // Hoist progress must not hide a FOUP the scheduler already committed to the OHT.
  let drawBox = isLoaded;

  if (isTransferActive && transferDuration) {
    const progress = Math.min(1.0, Math.max(0.0, stopElapsedS / transferDuration));
    if (stopLoadedAction === false && progress + 1e-6 >= OHT_TRACK_DROP_RELEASE_PROGRESS) {
      drawBox = false;
    } else if (
      !drawBox &&
      stopLoadedAction === true &&
      progress + 1e-6 >= OHT_TRACK_PICKUP_TAKE_PROGRESS
    ) {
      // Replay / one-frame lag before runtime carry reaches React state.
      drawBox = true;
    } else if (
      !drawBox &&
      stopLoadedAction === false &&
      progress + 1e-6 < OHT_TRACK_DROP_RELEASE_PROGRESS
    ) {
      drawBox = true;
    }
  }

  return (
    <group>
      {/* ========================================================================= */}
      {/* 1. FOUR METALLIC ROLLER WHEEL BOGIES (Riding directly on top of Track)   */}
      {/* ========================================================================= */}
      {[wheelFrontX, wheelRearX].map((wx, bIdx) => (
        <group key={`bogie-pair-${bIdx}`} position={[pX + wx, wheelY, pY]}>
          {/* Wheel Axle Crossbar connecting left and right sides */}
          <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.035 * S, 0.035 * S, trackRailW * 1.15, 16]} />
            <meshStandardMaterial color="#475569" metalness={0.94} roughness={0.18} />
          </mesh>

          {/* Left Bogie Unit (-Z face) */}
          <group position={[0, 0, -trackHalfW * 0.95]}>
            <WheelBogieUnit
              wheelRadius={wheelRadius}
              wheelWidth={wheelWidth}
              sideSign={-1}
              outerDirSign={bIdx === 0 ? 1 : -1}
              S={S}
            />
          </group>

          {/* Right Bogie Unit (+Z face) */}
          <group position={[0, 0, trackHalfW * 0.95]}>
            <WheelBogieUnit
              wheelRadius={wheelRadius}
              wheelWidth={wheelWidth}
              sideSign={1}
              outerDirSign={bIdx === 0 ? 1 : -1}
              S={S}
            />
          </group>
        </group>
      ))}

      {/* ========================================================================= */}
      {/* 2. TWO COMPACT ROUNDED WHITE LOWER PODS (Underneath Chassis on each side) */}
      {/* ========================================================================= */}
      {/* Left Side Pods (Facing -Z) */}
      <group position={[pX, podCenterY, pY - podOffsetZ]}>
        <group position={[podRearX, 0, 0]}>
          <WhiteLowerPod
            length={podL}
            height={podH}
            width={podW}
            opacity={opacity}
            badge="recess"
            facingSign={-1}
            S={S}
          />
        </group>
        <group position={[podFrontX, 0, 0]}>
          <WhiteLowerPod
            length={podL}
            height={podH}
            width={podW}
            opacity={opacity}
            badge="BLI"
            facingSign={-1}
            S={S}
          />
        </group>
      </group>

      {/* Right Side Pods (Facing +Z) */}
      <group position={[pX, podCenterY, pY + podOffsetZ]}>
        <group position={[podRearX, 0, 0]}>
          <WhiteLowerPod
            length={podL}
            height={podH}
            width={podW}
            opacity={opacity}
            badge="recess"
            facingSign={1}
            S={S}
          />
        </group>
        <group position={[podFrontX, 0, 0]}>
          <WhiteLowerPod
            length={podL}
            height={podH}
            width={podW}
            opacity={opacity}
            badge="BLI"
            facingSign={1}
            S={S}
          />
        </group>
      </group>

      {/* Center Metallic Mounting Bridge between the two white pods */}
      <group position={[pX, podCenterY + podH * 0.2, pY]}>
        {[-(podOffsetZ - podW * 0.45), podOffsetZ - podW * 0.45].map((cz, cIdx) => (
          <group key={`center-mount-${cIdx}`} position={[0, 0, cz]}>
            <mesh castShadow>
              <boxGeometry args={[0.36 * S, 0.22 * S, 0.06 * S]} />
              <meshStandardMaterial color="#D1D5DB" metalness={0.95} roughness={0.16} />
            </mesh>
            {/* Center Allen Bolt */}
            <mesh position={[0, 0, (cIdx === 0 ? -1 : 1) * 0.035 * S]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.035 * S, 0.035 * S, 0.015 * S, 12]} />
              <meshStandardMaterial color="#94A3B8" metalness={0.96} roughness={0.12} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ========================================================================= */}
      {/* 3. GLOSSY ROUNDED BLACK UPPER CHASSIS HOUSING                             */}
      {/* ========================================================================= */}
      <group position={[pX, chassisCenterY, pY]}>
        {/* Main Aerodynamic Glossy Black Body */}
        <mesh geometry={chassisGeometry} castShadow receiveShadow>
          <meshStandardMaterial
            color="#0F1215"
            metalness={0.88}
            roughness={0.16}
            envMapIntensity={1.8}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Lower Perimeter Seam Line */}
        <mesh position={[0, -chassisH * 0.35, 0]}>
          <boxGeometry args={[bodyL * 0.99, 0.015 * S, bodyW * 0.99]} />
          <meshStandardMaterial color="#050708" roughness={0.6} />
        </mesh>

        {/* Recessed Pill-Shaped Control Panel on the Long Side Face */}
        <group position={[0.12 * S, 0.02 * S, bodyW * 0.5 + 0.01 * S]}>
          {/* Dark Bezel Panel */}
          <mesh>
            <boxGeometry args={[bodyL * 0.32, chassisH * 0.44, 0.02 * S]} />
            <meshStandardMaterial color="#14171A" roughness={0.4} metalness={0.6} />
          </mesh>
          {/* Red E-Stop Circular Button */}
          <mesh position={[-0.26 * S, 0, 0.015 * S]} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.075 * S, 0.075 * S, 0.025 * S, 20]} />
            <meshStandardMaterial color="#EF4444" emissive="#DC2626" emissiveIntensity={0.6} roughness={0.2} />
          </mesh>
          {/* Horizontal Red Dash Status Light */}
          <mesh position={[0, 0, 0.015 * S]}>
            <boxGeometry args={[0.12 * S, 0.04 * S, 0.02 * S]} />
            <meshStandardMaterial color="#EF4444" emissive="#EF4444" emissiveIntensity={0.95} />
          </mesh>
          {/* Green Status LED Circle */}
          <mesh position={[0.26 * S, 0, 0.015 * S]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.075 * S, 0.075 * S, 0.025 * S, 20]} />
            <meshStandardMaterial color="#10B981" emissive="#10B981" emissiveIntensity={0.95} roughness={0.2} />
          </mesh>
        </group>

        {/* 4 White Alignment/Locating Pins on Top Deck */}
        {[-bodyL * 0.32, bodyL * 0.32].map((px, pidx) =>
          [-bodyW * 0.34, bodyW * 0.34].map((pz, pzidx) => (
            <mesh
              key={`deck-pin-${pidx}-${pzidx}`}
              position={[px, chassisH * 0.5 + 0.04 * S, pz]}
              castShadow
            >
              <cylinderGeometry args={[0.035 * S, 0.035 * S, 0.08 * S, 12]} />
              <meshStandardMaterial color="#F8FAFC" metalness={0.1} roughness={0.2} />
            </mesh>
          ))
        )}

        {/* ========================================================================= */}
        {/* 4. TRANSPARENT ACRYLIC/POLYCARBONATE TOP COVER & MOTOR TURNTABLE          */}
        {/* ========================================================================= */}
        <group position={[0, chassisH * 0.5 + 0.11 * S, 0]}>
          {/* Clear Molded Polycarbonate Top Bubble */}
          <mesh geometry={topCoverGeometry} castShadow>
            <meshPhysicalMaterial
              color="#FFFFFF"
              transmission={0.88}
              opacity={0.45}
              transparent
              roughness={0.08}
              metalness={0.1}
              ior={1.5}
            />
          </mesh>

          {/* Internal Concentric Rotary Motor Drive Turntable */}
          <group position={[0, -0.02 * S, 0]}>
            <mesh rotation={[-Math.PI / 2, 0, 0]} castShadow>
              <ringGeometry args={[0.25 * S, 0.95 * S, 36]} />
              <meshStandardMaterial color="#1E2328" roughness={0.3} metalness={0.8} />
            </mesh>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01 * S, 0]}>
              <ringGeometry args={[0.5 * S, 0.75 * S, 36]} />
              <meshStandardMaterial color="#2B3440" roughness={0.4} metalness={0.6} />
            </mesh>
            <mesh position={[0, 0.03 * S, 0]}>
              <cylinderGeometry args={[0.2 * S, 0.2 * S, 0.08 * S, 24]} />
              <meshStandardMaterial color="#0A0C0E" roughness={0.25} metalness={0.9} />
            </mesh>
          </group>

          {/* 4 Fluted/Ribbed Clear Corner Cylinders with White Nylon Fasteners */}
          {[-bodyL * 0.28, bodyL * 0.28].map((tx, tidx) =>
            [-bodyW * 0.3, bodyW * 0.3].map((tz, tzidx) => (
              <group key={`corner-tower-${tidx}-${tzidx}`} position={[tx, 0, tz]}>
                <mesh castShadow>
                  <cylinderGeometry args={[0.085 * S, 0.085 * S, 0.22 * S, 16]} />
                  <meshPhysicalMaterial
                    color="#FFFFFF"
                    transmission={0.85}
                    opacity={0.55}
                    transparent
                    roughness={0.1}
                    ior={1.5}
                  />
                </mesh>
                <mesh position={[0, 0.12 * S, 0]}>
                  <cylinderGeometry args={[0.03 * S, 0.03 * S, 0.05 * S, 8]} />
                  <meshStandardMaterial color="#F1F5F9" roughness={0.3} />
                </mesh>
              </group>
            ))
          )}
        </group>
      </group>

      {/* ========================================================================= */}
      {/* 5. FLOATING STATUS BADGE ([ Idle ] / Phase)                               */}
      {/* ========================================================================= */}
      <OhtPhaseIndicator3D
        status={status}
        tipChip={tipChip}
        label={statusLabel}
        opacity={opacity}
        scale={S}
        vScale={vScale}
        pivotX={pX}
        pivotY={pY}
        width={w}
        height={h}
        bodyTopY={vehicleBodyTopY}
        showBadge={showStatusBadge}
      />

      {/* ========================================================================= */}
      {/* 6. FOUP CASSETTE ON OHT VEHICLE (NO LOWERING LEGS / CABLES)              */}
      {/* ========================================================================= */}
      {drawBox && (
        <group position={[pX, chassisCenterY + 0.4 * S, pY]}>
          <FoupBox3D w={w} h={h} opacity={opacity} S={S} foupKind={foupKind} />
        </group>
      )}
    </group>
  );
});

export default OhtVehicle3DModel;
